'use client';

import React from 'react';
import { MapPin, AlertTriangle, Send } from 'lucide-react';
import type { GPSLocation } from '../types';
import type { ChildEntryType } from '../types';

export interface PoFulfillmentInfo {
  isFullyDelivered?: boolean;
  lineItems: Array<{ remainingQty: number }>;
}

export interface BottomActionBarProps {
  isFormVisible: boolean;
  receiptPhotos: unknown[];
  submitting: boolean;
  receiptPhotoProcessing: boolean;
  isCasualWage: boolean;
  receiptValidating: boolean;
  gpsLocation: GPSLocation | null | undefined;
  selectedEntryType: ChildEntryType;
  poFulfillment: PoFulfillmentInfo | null;
  missingRequiredFields: string[];
  requiredComplete: boolean;
  receiptCategorySeverity: 'blocking' | 'advisory' | null;
  hasUnacknowledgedDuplicates: boolean;
  /** When the AI couldn't determine the document type for a supplier-linked
   *  expense, show a blocking banner with 3 buttons: Invoice / Quote / Delivery
   *  Note. The user must pick one before the submit button is enabled. */
  pendingDocTypeDetermination?: boolean;
  /** Called when the user picks a document type from the pending banner. */
  onPickDocType?: (type: 'supplier_invoice' | 'supplier_quote' | 'delivery_note') => void;
  /** When the AI-detected supplier name doesn't match the technician's selected
   *  supplier, block submit until acknowledged. */
  hasUnacknowledgedSupplierMismatch?: boolean;
  accent: string;
  isDark: boolean;
  onSubmit: () => void;
}

export function BottomActionBar({
  isFormVisible,
  receiptPhotos,
  submitting,
  receiptPhotoProcessing,
  isCasualWage,
  receiptValidating,
  gpsLocation,
  selectedEntryType,
  poFulfillment,
  missingRequiredFields,
  requiredComplete,
  receiptCategorySeverity,
  hasUnacknowledgedDuplicates,
  pendingDocTypeDetermination,
  onPickDocType,
  hasUnacknowledgedSupplierMismatch,
  accent,
  isDark,
  onSubmit,
}: BottomActionBarProps) {
  // Travel/mileage expenses don't require a receipt photo — the logbook trip data is the evidence.
  const requiresPhoto = selectedEntryType !== 'travel';
  if (!isFormVisible || (requiresPhoto && receiptPhotos.length === 0) || submitting || receiptPhotoProcessing || (!isCasualWage && receiptValidating)) {
    return null;
  }

  return (
    <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-3 pt-3 pb-3">
      <div className="max-w-4xl mx-auto">
        {gpsLocation && (
          <div className="flex items-center justify-center gap-1 mb-2">
            <MapPin className="w-3 h-3 text-gray-400" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
            </span>
          </div>
        )}
        {selectedEntryType === 'delivery_note' && poFulfillment?.isFullyDelivered && (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs flex items-center gap-2 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 text-red-700">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              This purchase order is fully delivered. No further delivery notes can be submitted.
            </span>
          </div>
        )}
        {missingRequiredFields.length > 0 && (
          <div className="mb-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-xs flex items-start gap-2 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 text-red-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Please complete these required fields to save:</p>
              <p className="mt-0.5 font-medium">{missingRequiredFields.join(', ')}</p>
            </div>
          </div>
        )}
        {hasUnacknowledgedDuplicates && (
          <div className="mb-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-xs flex items-start gap-2 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 text-red-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Duplicate expense detected</p>
              <p className="mt-0.5">Acknowledge the duplicate warning above before saving.</p>
            </div>
          </div>
        )}
        {pendingDocTypeDetermination && onPickDocType && (
          <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">What type of supplier document is this?</p>
                <p className="mt-0.5 text-amber-700 dark:text-amber-300">AI could not determine the document type. Please select one.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onPickDocType('supplier_invoice')}
                className="flex-1 py-2 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              >
                Invoice
              </button>
              <button
                onClick={() => onPickDocType('supplier_quote')}
                className="flex-1 py-2 rounded-lg text-xs font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors"
              >
                Quote
              </button>
              <button
                onClick={() => onPickDocType('delivery_note')}
                className="flex-1 py-2 rounded-lg text-xs font-semibold bg-orange-600 text-white hover:bg-orange-700 transition-colors"
              >
                Delivery Note
              </button>
            </div>
          </div>
        )}
        {hasUnacknowledgedSupplierMismatch && (
          <div className="mb-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2.5 text-xs flex items-start gap-2 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-200 text-orange-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Supplier mismatch detected</p>
              <p className="mt-0.5">The AI detected a different supplier name. Acknowledge the mismatch warning above before saving.</p>
            </div>
          </div>
        )}
        <button
          onClick={onSubmit}
          data-tour-id="smarttask-child-entry-submit"
          disabled={
            !requiredComplete ||
            (selectedEntryType === 'delivery_note' && poFulfillment?.isFullyDelivered) ||
            receiptCategorySeverity === 'blocking' ||
            hasUnacknowledgedDuplicates ||
            pendingDocTypeDetermination ||
            hasUnacknowledgedSupplierMismatch
          }
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: accent }}
        >
          <Send className="w-4 h-4" />
          {hasUnacknowledgedSupplierMismatch
            ? 'Acknowledge supplier mismatch to save'
            : pendingDocTypeDetermination
            ? 'Select document type to save'
            : hasUnacknowledgedDuplicates
            ? 'Acknowledge duplicate to save'
            : missingRequiredFields.length > 0
            ? 'Complete required fields to save'
            : 'Save'}
        </button>
      </div>
    </div>
  );
}
